import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ScrollText, RefreshCw, Search, X } from "lucide-react";
import api from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ACTION_LABELS = {
  "business.create": { txt: "Buat Bisnis", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  "business.update_config": { txt: "Ubah Konfigurasi", cls: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  "business.delete": { txt: "Hapus Bisnis", cls: "bg-destructive/10 text-destructive border-destructive/20" },
  "business.transfer": { txt: "Transfer Bisnis", cls: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  "database.update_config": { txt: "Ubah Database", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  "database.test_connection": { txt: "Test Koneksi", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  "admin.create": { txt: "Buat Admin", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  "admin.invite": { txt: "Undang Admin", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  "admin.delete": { txt: "Hapus Admin", cls: "bg-destructive/10 text-destructive border-destructive/20" },
  "admin.reset_password": { txt: "Reset Password", cls: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  "security.rotate_key": { txt: "Rotasi Kunci", cls: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/activity-logs");
    setLogs(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const actors = useMemo(() => [...new Set(logs.map((l) => l.actor_email))], [logs]);
  const actions = useMemo(() => [...new Set(logs.map((l) => l.action))], [logs]);

  const filtered = useMemo(() => logs.filter((l) => {
    if (actor !== "all" && l.actor_email !== actor) return false;
    if (action !== "all" && l.action !== action) return false;
    if (q) {
      const hay = `${l.actor_email} ${l.action} ${l.target} ${l.detail}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    const day = String(l.created_at || "").slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  }), [logs, q, actor, action, from, to]);

  const resetFilters = () => { setQ(""); setActor("all"); setAction("all"); setFrom(""); setTo(""); };
  const hasFilter = q || actor !== "all" || action !== "all" || from || to;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4 grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto_auto] items-end">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Pencarian</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari target/detail..." className="pl-9" />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Admin</label>
          <Select value={actor} onValueChange={setActor}>
            <SelectTrigger data-testid="log-filter-actor" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Admin</SelectItem>
              {actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Aksi</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="log-filter-action" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Aksi</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a]?.txt || a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Dari</label>
          <Input data-testid="log-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Sampai</label>
          <Input data-testid="log-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36" />
        </div>
        <div className="flex gap-2">
          {hasFilter && <Button variant="ghost" size="icon" onClick={resetFilters} data-testid="log-clear-filters"><X className="w-4 h-4" /></Button>}
          <Button variant="outline" size="icon" onClick={load} data-testid="refresh-logs"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ScrollText className="w-4 h-4" /> Menampilkan {filtered.length} dari {logs.length} aktivitas
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground p-8 text-center">
            {loading ? "Memuat..." : "Tidak ada aktivitas yang cocok."}
          </p>
        ) : (
          <Table data-testid="activity-log-table">
            <TableHeader><TableRow>
              <TableHead>Waktu</TableHead><TableHead>Admin</TableHead>
              <TableHead>Aksi</TableHead><TableHead>Target</TableHead><TableHead>Detail</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((l, i) => {
                const a = ACTION_LABELS[l.action] || { txt: l.action, cls: "bg-muted text-muted-foreground" };
                return (
                  <motion.tr key={l.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.015, 0.3) }}
                    className="border-b border-border last:border-0" data-testid="activity-log-row">
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                    <TableCell className="text-sm font-mono">{l.actor_email}</TableCell>
                    <TableCell><Badge variant="outline" className={a.cls}>{a.txt}</Badge></TableCell>
                    <TableCell className="text-sm">{l.target || "-"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{l.detail}</TableCell>
                  </motion.tr>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

